import { getProfileViewsMetrics } from "@/infrastructure/services/metricsService";
import type { MetricsPeriod, ProfileViewsByDay } from "@/types/profileMetrics";
import { Dialog, Dropdown, Loading, Tooltip } from "@uigovpe/components";
import { useEffect, useMemo, useState } from "react";

type Props = {
  visible: boolean;
  onHide: () => void;
};

const periods = [
  { label: "Últimos 7 dias", value: "7d" },
  { label: "Últimos 30 dias", value: "30d" },
];

const toneClasses = {
  low: "fill-[#e5e7eb]!",
  medium: "fill-[#cec7f4]!",
  high: "fill-[#4e3873]!",
};

export default function ProfileViewsDialog({ visible, onHide }: Props) {
  const [period, setPeriod] = useState<MetricsPeriod>("7d");
  const [dailyViews, setDailyViews] = useState<ProfileViewsByDay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setIsLoading(true);
    setError(null);
    getProfileViewsMetrics(period)
      .then((data) => active && setDailyViews(data.visualizacoesPorDia ?? []))
      .catch(() => {
        if (active) setError("Não foi possível carregar as visualizações.");
      })
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [period, visible]);

  const days = useMemo(() => {
    const max = Math.max(...dailyViews.map((item) => item.total), 0);
    return dailyViews.map((item) => {
      const ratio = max > 0 ? item.total / max : 0;
      const tone = ratio > 0.75 ? "high" : ratio > 0.4 ? "medium" : "low";
      const date = new Date(`${item.data}T12:00:00`);
      const day = new Intl.DateTimeFormat("pt-BR", { weekday: "short" })
        .format(date)
        .replace(".", "")
        .toUpperCase();
      const dateLabel = new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(date);
      return { day, dateLabel, value: item.total, tone } as const;
    });
  }, [dailyViews]);

  const maxValue = Math.max(...days.map((item) => item.value), 1);

  return (
    <Dialog
      visible={visible}
      header={
        <div className="grid! w-full! gap-4! sm:grid-cols-[minmax(0,1fr)_minmax(14rem,16.25rem)]! sm:items-start! sm:gap-8!">
          <span className="pt-1! text-[20px]! leading-7! font-bold! text-[#494c57]!">
            Visualizações do perfil por dia
          </span>
          <div className="w-full!">
            <Dropdown
              label="Selecione o Período"
              options={periods}
              value={period}
              onChange={(event) => setPeriod(event.value as MetricsPeriod)}
              disabled={isLoading}
              className="profile-views-period w-full!"
            />
          </div>
        </div>
      }
      onHide={onHide}
      draggable={false}
      closable={false}
      className="profile-views-dialog w-[min(94vw,760px)]! bg-white!"
    >
      <section className="rounded-2xl! px-4! py-0! sm:px-6! sm:py-3!">
        {isLoading ? (
          <div
            className="flex! h-64! flex-col! items-center! justify-center! gap-3!"
            role="status"
            aria-live="polite"
            aria-label="Carregando visualizações do perfil"
          >
            <Loading className="text-[#4e3873]!" />
            <span className="text-sm! font-medium! text-[#494c57]!">
              Carregando visualizações...
            </span>
          </div>
        ) : error ? (
          <div
            className="flex! h-64! items-center! justify-center! text-sm! text-red-700!"
            role="alert"
          >
            {error}
          </div>
        ) : days.length === 0 ? (
          <div className="flex! h-64! items-center! justify-center! text-sm! text-[#6b7280]!">
            Nenhuma visualização no período selecionado.
          </div>
        ) : (
          <div className="flex! h-64! items-end! justify-between! gap-2! overflow-x-auto! border-b! border-[#e5e7eb]! px-2! pb-4! sm:px-6!">
            {days.map(({ day, dateLabel, value, tone }, index) => {
              const barHeight = Math.max(20, (value / maxValue) * 125);
              const barTop = 165 - barHeight;

              return (
                <div
                  key={`${day}-${index}`}
                  className="flex! h-full! min-w-12! flex-1! flex-col! items-center! justify-end!"
                >
                  <svg
                    viewBox="0 0 40 165"
                    className="h-[165px]! w-full! max-w-10! overflow-visible!"
                    preserveAspectRatio="none"
                  >
                    <rect
                      x="0.5"
                      y={barTop - 40}
                      width="39"
                      height="28"
                      rx="5"
                      className={
                        tone === "low"
                          ? "fill-white! stroke-[#d1d5db]! drop-shadow-sm!"
                          : "fill-[#4e3873]! stroke-[#4e3873]! drop-shadow-sm!"
                      }
                    />
                    <text
                      x="20"
                      y={barTop - 26}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className={`text-[13px]! font-bold! ${tone === "low" ? "fill-[#4e3873]!" : "fill-white!"}`}
                    >
                      {value}
                    </text>
                    <rect
                      x="0"
                      y={barTop}
                      width="40"
                      height={barHeight}
                      rx="4"
                      className={toneClasses[tone]}
                    />
                    <rect
                      id={`profile-view-bar-${index}`}
                      tabIndex={0}
                      role="img"
                      aria-label={`${dateLabel}: ${value} ${value === 1 ? "visualização" : "visualizações"}`}
                      x="0"
                      y={barTop - 40}
                      width="40"
                      height={barHeight + 40}
                      className="fill-transparent!"
                    />
                  </svg>
                  <Tooltip
                    target={`#profile-view-bar-${index}`}
                    theme="light"
                    position="top"
                    content={<span className="capitalize">{dateLabel}</span>}
                  />
                  <strong className="mt-2! text-[10px]! text-[#28272c]!">
                    {day}
                  </strong>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-5! flex! flex-wrap! justify-center! gap-x-6! gap-y-2! text-[11px]! text-[#6b7280]!">
          <span className="flex! items-center! gap-2!">
            <i className="h-3! w-3! rounded-full! bg-[#d1d5db]!" />
            Pouco volume de visitas
          </span>
          <span className="flex! items-center! gap-2!">
            <i className="h-3! w-3! rounded-full! bg-[#cec7f4]!" />
            Médio volume de visitas
          </span>
          <span className="flex! items-center! gap-2!">
            <i className="h-3! w-3! rounded-full! bg-[#4e3873]!" />
            Maior volume de visitas
          </span>
        </div>
      </section>
    </Dialog>
  );
}
